/**
 * jp-tooltip.ts — hover tooltip for linked jump points in tactical view.
 *
 * Reads UiState.hoveredJumpPointId (set by input.ts on mouse-move) and, if
 * non-null, shows a small DOM panel near the cursor with:
 *   - destination system name
 *   - total fuel cost for the ships that would jump
 *   - ship count participating in the jump
 *
 * "Ships that would jump" matches the same interim selection rule input.ts
 * uses for click-to-jump: every player ship currently in the focused system.
 * When real ship-selection UI lands the rule moves to "selected ships", and
 * this module can ride that change without layout churn.
 *
 * Pure reader — the tooltip never mutates state.
 */

import type { GameState } from '../state/types';
import type { UiState } from './state';
import { fuelCostForJump } from '../game/commands';

export interface JpTooltipHandle {
  update(state: GameState, ui: UiState): void;
  destroy(): void;
}

export function mountJpTooltip(parent: HTMLElement): JpTooltipHandle {
  const root = document.createElement('div');
  root.id = 'jp-tooltip';
  Object.assign(root.style, {
    position: 'fixed',
    // Positioned per-frame via update(); hidden by default.
    left: '0px',
    top: '0px',
    padding: '6px 8px',
    background: 'rgba(10, 14, 24, 0.92)',
    border: '1px solid rgba(120, 160, 230, 0.4)',
    borderRadius: '3px',
    color: '#d8e4ff',
    font: '11px / 1.35 monospace',
    // Tooltip never intercepts mouse events — the JP under the cursor has to
    // stay hoverable, and clicks must fall through to the canvas.
    pointerEvents: 'none',
    zIndex: '20',
    display: 'none',
    whiteSpace: 'nowrap',
  } as Partial<CSSStyleDeclaration>);
  parent.appendChild(root);

  return {
    update(state: GameState, ui: UiState): void {
      const jpId = ui.hoveredJumpPointId;
      const anchor = ui.hoveredJumpPointAnchor;
      if (jpId === null || !anchor || ui.viewMode !== 'system') {
        root.style.display = 'none';
        return;
      }
      const jp = state.jumpPoints.get(jpId);
      if (!jp || jp.linkedSystemId === null) {
        root.style.display = 'none';
        return;
      }
      const destSys = state.systems.get(jp.linkedSystemId);
      if (!destSys) {
        root.style.display = 'none';
        return;
      }
      const focusedSystemId = ui.focusedSystemId;
      if (focusedSystemId === null) {
        root.style.display = 'none';
        return;
      }
      // Collect the ships that would jump (same rule as click-to-jump).
      const shipIds: number[] = [];
      for (const ship of state.ships.values()) {
        if (
          ship.ownerEmpireId === state.playerEmpireId &&
          ship.systemId === focusedSystemId
        ) {
          shipIds.push(ship.id);
        }
      }
      const fuel = fuelCostForJump(state, shipIds);
      const empire = state.empires.get(state.playerEmpireId);
      const have = empire ? empire.resources.fuel : 0;
      const affordable = have >= fuel;
      const costColour = affordable ? '#8fffb0' : '#ff8f8f';

      root.innerHTML =
        `<div style="color:#cfe0ff"><strong>Jump to ${escapeHtml(destSys.name)}</strong></div>` +
        `<div>ships: ${shipIds.length}</div>` +
        `<div>fuel: <span style="color:${costColour}">${fuel.toFixed(1)}</span> / ${have.toFixed(1)}</div>` +
        (shipIds.length === 0
          ? `<div style="color:#ff8f8f">no ships in this system</div>`
          : '');

      // Offset from cursor so the tooltip doesn't sit under the pointer and
      // block future mouse-move hit-tests against the JP.
      root.style.left = `${anchor.x + 12}px`;
      root.style.top = `${anchor.y + 12}px`;
      root.style.display = 'block';
    },
    destroy(): void {
      root.remove();
    },
  };
}

/** Minimal escaper — system names could in theory contain HTML-unsafe chars. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
